import { lazy, Suspense, useEffect } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { AuthProvider } from '@/stores/auth-context'
import { ToastProvider } from '@/components/ui/Toast'
import { NotificationProvider } from '@/stores/notification-context'

// Layouts
import DashboardLayout from '@/components/layouts/DashboardLayout'

// Guards
import ProtectedRoute from '@/components/guards/ProtectedRoute'
import RoleGuard from '@/components/guards/RoleGuard'
import ForbiddenPage from '@/components/guards/ForbiddenPage'
import NotFoundPage from '@/components/guards/NotFoundPage'

// ── Lazy-loaded public + auth + student pages ───────────────────────────────
const HomePage = lazy(() => import('@/features/dashboard/homepage/HomePage'))
const Reviews = lazy(() => import('@/components/Reviews'))
const Contact = lazy(() => import('@/components/Contact'))

const Login = lazy(() => import('@/features/auth/Login'))
const Register = lazy(() => import('@/features/auth/Register'))
const VerifyEmail = lazy(() => import('@/features/auth/VerifyEmail'))
const ForgotPassword = lazy(() => import('@/features/auth/ForgotPassword'))
const ResetPassword = lazy(() => import('@/features/auth/ResetPassword'))

const DashboardHome = lazy(() => import('@/features/dashboard/student-dashboard/DashboardHome'))
const MyCourses = lazy(() => import('@/features/dashboard/student-dashboard/MyCourses'))
const StudentMaterialsPage = lazy(() => import('@/features/dashboard/student-dashboard/StudentMaterialsPage'))
const PaymentHistory = lazy(() => import('@/features/dashboard/student-dashboard/PaymentHistory'))
const DailyTasks = lazy(() => import('@/features/dashboard/student-dashboard/DailyTasks'))
const ProgressReport = lazy(() => import('@/features/dashboard/student-dashboard/ProgressReport'))

const FreeHub = lazy(() => import('@/features/freehub/FreeHub'))
const FreeHubWatchPage = lazy(() => import('@/features/freehub/FreeHubWatchPage'))
const CodingPlaygroundPage = lazy(() => import('@/features/playground/CodingPlaygroundPage')) // ← TICKET-09

const CoursesPage = lazy(() => import('@/features/courses/CoursesPage')) // ← TICKET-06
const CourseDetailPage = lazy(() => import('@/features/courses/CourseDetailPage')) // ← TICKET-07
const PythonCourseTeluguPage = lazy(() => import('@/features/courses/PythonCourseTeluguPage'))
const TopicLearningPage = lazy(() => import('@/features/courses/TopicLearningPage')) // ← TICKET-08
const CourseLMS = lazy(() => import('@/features/courses/CourseLMS'))
const Certificates = lazy(() => import('@/features/courses/Certificates'))
const ProjectsPage = lazy(() => import('@/features/projects/ProjectsPage'))

const OpportunitiesPage = lazy(() => import('@/features/opportunities/OpportunitiesPage'))
const OpportunityDetailPage = lazy(() => import('@/features/opportunities/OpportunityDetailPage'))
const DoubtSessionsPage = lazy(() => import('@/features/doubt-sessions/DoubtSessionsPage'))

const RoadmapListPage = lazy(() => import('@/features/roadmaps/RoadmapListPage'))
const RoadmapDetailPage = lazy(() => import('@/features/roadmaps/RoadmapDetailPage'))

const PrivacyPage = lazy(() => import('@/features/legal/PrivacyPage'))
const TermsPage = lazy(() => import('@/features/legal/TermsPage'))
const RefundPolicyPage = lazy(() => import('@/features/legal/RefundPolicyPage'))

const AdminRoadmapListPage = lazy(() => import('@/features/admin/AdminRoadmapListPage'))
const AdminRoadmapCreatePage = lazy(() => import('@/features/admin/AdminRoadmapCreatePage'))
const AdminRoadmapEditPage = lazy(() => import('@/features/admin/AdminRoadmapEditPage'))
// ── Lazy-loaded Admin ───────────────────────────────────────────────────────
const AdminLayout = lazy(() => import('@/components/layouts/AdminLayout'))
const AdminDashboard = lazy(() => import('@/features/admin/AdminDashboard'))
const UsersList = lazy(() => import('@/features/admin/users/UsersList'))
const UserDetail = lazy(() => import('@/features/admin/users/UserDetail'))
const CreateUserForm = lazy(() => import('@/features/admin/users/CreateUserForm'))
const CoursesList = lazy(() => import('@/features/admin/courses/CoursesList'))
const AdminMaterialsIndexPage = lazy(() => import('@/features/admin/courses/AdminMaterialsIndexPage'))
const AdminEnrollmentsPage = lazy(() => import('@/features/admin/enrollments/AdminEnrollmentsPage'))
const ReportsPage = lazy(() => import('@/features/admin/reports/ReportsPage'))
const CertificatesList = lazy(() => import('@/features/admin/certificates/CertificatesList'))
const AdminProfile = lazy(() => import('@/features/admin/profile/AdminProfile'))
const ModulesPage = lazy(() => import('@/features/admin/courses/ModulesPage'))

// ── Lazy-loaded Student ───────────────────────────────────────────────────────
const StudentProfile = lazy(() => import('@/features/dashboard/student-dashboard/StudentProfile'))
const PaymentsPage = lazy(() => import('@/features/admin/payments/PaymentsPage'))
const OpportunitiesList = lazy(() => import('@/features/admin/opportunities/OpportunitiesList'))
const AdminEnquiriesPage = lazy(() => import('@/features/admin/enquiries/AdminEnquiriesPage'))
const FreeHubCoursesList = lazy(() => import('@/features/admin/free-hub/FreeHubCoursesList'))
const DoubtSessionsList = lazy(() => import('@/features/admin/doubt-sessions/DoubtSessionsList'))
const CourseCreatePage = lazy(() => import('@/features/admin/courses/CourseCreatePage'))
const CourseEditPage = lazy(() => import('@/features/admin/courses/CourseEditPage'))
const AdminTopicsPage = lazy(() => import('@/features/admin/topics/AdminTopicsPage'))
const AdminContentPage = lazy(() => import('@/features/admin/courses/AdminContentPage'))
const AdminResourcesPage = lazy(() => import('@/features/admin/resources/AdminResourcesPage'))
const AdminResourcesIndexPage = lazy(() => import('@/features/admin/resources/AdminResourcesIndexPage'))
const CourseResourcesPage = lazy(() => import('@/features/admin/resources/CourseResourcesPage'))
const AdminProjectsPage = lazy(() => import('@/features/admin/projects/AdminProjectsPage'))
const AdminSubmissionsPage = lazy(() => import('@/features/admin/projects/AdminSubmissionsPage'))
const AdminDailyTasksPage = lazy(() => import('@/features/admin/daily-tasks/AdminDailyTasksPage'))
const AdminInterviewQuestionsPage = lazy(() => import('@/features/admin/interview-questions/AdminInterviewQuestionsPage'))
const AnalyticsPage = lazy(() => import('@/features/admin/analytics/AnalyticsPage'))
const MaterialsPage = lazy(() => import('@/features/admin/courses/MaterialsPage'))


// ── Loading fallback ────────────────────────────────────────────────────────
function PageLoader() {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="h-6 w-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
  )
}

function ScrollToTop() {
  const { pathname } = useLocation()
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [pathname])
  return null
}

function App() {
  return (
    <AuthProvider>
      <NotificationProvider>
      <ToastProvider>
      <BrowserRouter>
        <ScrollToTop />
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* ── Homepage ────────────────────────────────────── */}
            <Route path="/" element={<HomePage />} />
            <Route path="/reviews" element={<Reviews />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/refund-policy" element={<RefundPolicyPage />} />
            <Route path="/free-hub" element={<FreeHub />} />
            <Route path="/free-hub/:slug" element={<FreeHubWatchPage />} />

            {/* ── Auth ────────────────────────────────────────── */}
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />

            {/* ── Roadmaps (public) ────────────────────────────── */}
            <Route path="/roadmaps" element={<RoadmapListPage />} />
            <Route path="/roadmaps/:title" element={<RoadmapDetailPage />} />
            {/* ── Courses (public) ────────────────────────────── */}
            <Route path="/courses" element={<CoursesPage />} /> {/* ← TICKET-06 */}
            <Route path="/courses/:slug" element={<CourseDetailPage />} /> {/* ← TICKET-07 */}
            <Route path="/python-course-in-telugu" element={<PythonCourseTeluguPage />} />

            {/* ── Student Dashboard (students only — admins are redirected to /admin) ── */}
            <Route element={<ProtectedRoute />}>
              <Route element={<RoleGuard allowedRoles={['student']} />}>
                <Route path="/dashboard" element={<DashboardLayout />}>
                  <Route index element={<DashboardHome />} />
                  <Route path="courses" element={<MyCourses />} />
                  <Route path="materials" element={<StudentMaterialsPage />} />
                  <Route path="certificates" element={<Certificates />} />
                  <Route path="projects" element={<ProjectsPage />} />
                  <Route path="payments" element={<PaymentHistory />} />
                  <Route path="opportunities" element={<OpportunitiesPage />} />
                  <Route path="opportunities/:id" element={<OpportunityDetailPage />} />
                  <Route path="doubt-sessions" element={<DoubtSessionsPage />} />
                  <Route path="daily-tasks" element={<DailyTasks />} />
                  <Route path="progress-report" element={<ProgressReport />} />
                  <Route path="profile" element={<StudentProfile />} />
                  <Route path="settings" element={<Navigate to="/dashboard/profile" replace />} />
                </Route>

                {/* ── Course LMS (students only) ──── */}
                <Route path="/lms/course/:slug" element={<CourseLMS />} />
                {/* ── Per-topic learning page (students only) ─────── */}
                <Route path="/learn/:courseId/:topicId" element={<TopicLearningPage />} />
                {/* ── Coding Playground (students only) ───────────── */}
                <Route path="/playground" element={<CodingPlaygroundPage />} />
              </Route>
            </Route>

            {/* ── Admin Panel ─────────────────────────────────── */}
            <Route element={<ProtectedRoute />}>
              <Route element={<RoleGuard allowedRoles={['admin']} />}>
                {/* ── Admin Topic Learning (outside layout) ──── */}
                <Route path="/admin/learn/:courseId/:topicId" element={<TopicLearningPage />} />

                <Route path="/admin" element={<AdminLayout />}>
                  <Route index element={<AdminDashboard />} />
                  <Route path="users" element={<UsersList />} />
                  <Route path="users/create" element={<CreateUserForm />} />
                  <Route path="users/:userId" element={<UserDetail />} />
                  <Route path="courses" element={<CoursesList />} />
                  <Route path="materials" element={<AdminMaterialsIndexPage />} />
                  <Route path="courses/create" element={<CourseCreatePage />} />
                  <Route path="courses/:courseId/edit" element={<CourseEditPage />} />
                  <Route path="courses/:courseId/modules/:moduleId/topics" element={<AdminTopicsPage />} />
                  <Route path="courses/:courseId/modules/:moduleId/topics/:topicId/content" element={<AdminContentPage />} />
                  <Route path="courses/:courseId/modules/:moduleId/topics/:topicId/resources" element={<AdminResourcesPage />} />
                  <Route path="courses/:courseId/modules" element={<ModulesPage />} />
                  <Route path="courses/:courseId/materials" element={<MaterialsPage />} />
                  <Route path="courses/:courseId/resources" element={<CourseResourcesPage />} />
                  <Route path="courses/:courseId/projects" element={<AdminProjectsPage />} />
                  <Route path="courses/:courseId/projects/:projectId/submissions" element={<AdminSubmissionsPage />} />
                  <Route path="resources" element={<AdminResourcesIndexPage />} />
                  <Route path="enrollments" element={<AdminEnrollmentsPage />} />
                  <Route path="daily-tasks" element={<AdminDailyTasksPage />} />
                  <Route path="interview-questions" element={<AdminInterviewQuestionsPage />} />
                  <Route path="certificates" element={<CertificatesList />} />
                  <Route path="payments" element={<PaymentsPage />} />
                  <Route path="opportunities" element={<OpportunitiesList />} />
                  <Route path="enquiries" element={<AdminEnquiriesPage />} />
                  <Route path="free-hub" element={<FreeHubCoursesList />} />
                  <Route path="doubt-sessions" element={<DoubtSessionsList />} />
                  <Route path="analytics" element={<AnalyticsPage />} />
                  <Route path="reports" element={<ReportsPage />} />
                  <Route path="profile" element={<AdminProfile />} />
                  <Route path="settings" element={<Navigate to="/admin/profile" replace />} />
                  <Route path="roadmaps" element={<AdminRoadmapListPage />} />
                  <Route path="roadmaps/create" element={<AdminRoadmapCreatePage />} />
                  <Route path="roadmaps/:title/edit" element={<AdminRoadmapEditPage />} />
                </Route>
              </Route>
            </Route>

            {/* ── 403 Forbidden ───────────────────────────────── */}
            <Route path="/forbidden" element={<ForbiddenPage />} />

            {/* ── 404 ─────────────────────────────────────────── */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
      </ToastProvider>
      </NotificationProvider>
    </AuthProvider>
  )
}

export default App
